import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdPaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState('failed');
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const max = 6;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/ads/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          return;
        }
        attempts += 1;
        if (attempts >= max) {
          setState('failed');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (cancelled) return;
        attempts += 1;
        if (attempts >= max) setState('failed');
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32 flex items-center justify-center px-4" data-testid="ad-payment-success-page">
      <div className="max-w-lg w-full bg-[#1A1A1A] border border-[#262626] rounded-lg p-8 text-center">
        {state === 'checking' && (
          <>
            <Loader size={48} className="text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-['Bebas_Neue'] tracking-wider text-white mb-2">Confirming payment...</h1>
            <p className="text-[#A3A3A3]">Please wait while we verify your transaction with Stripe.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 size={48} className="text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-['Bebas_Neue'] tracking-wider text-white mb-2">Payment received</h1>
            <p className="text-[#A3A3A3] mb-2">Thank you. Your ad submission is now in <strong className="text-white">manual review</strong>.</p>
            <p className="text-sm text-[#737373] mb-6">You'll get an email once the artist approves or requests changes. If rejected, we'll issue a full refund automatically.</p>
            {details?.amount_total && (
              <p className="text-xs text-[#A3A3A3]">
                Charged: ${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
              </p>
            )}
          </>
        )}
        {state === 'failed' && (
          <>
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-['Bebas_Neue'] tracking-wider text-white mb-2">Payment status unclear</h1>
            <p className="text-[#A3A3A3] mb-6">If you completed payment, please email us at <a href="mailto:admin@iamhethatis.com" className="text-[#D4AF37] underline">admin@iamhethatis.com</a> with your booking details.</p>
          </>
        )}
        {state === 'expired' && (
          <>
            <XCircle size={48} className="text-yellow-400 mx-auto mb-4" />
            <h1 className="text-2xl font-['Bebas_Neue'] tracking-wider text-white mb-2">Session expired</h1>
            <p className="text-[#A3A3A3] mb-6">Your payment session timed out. You can resubmit your booking anytime.</p>
          </>
        )}
        <Link to="/advertise" className="inline-block mt-2 text-sm text-[#D4AF37] underline" data-testid="ad-payment-back-link">
          Back to Advertise page
        </Link>
      </div>
    </div>
  );
};

export default AdPaymentSuccessPage;
